/**
 * The access token, and everything that decides when it is replaced.
 *
 * One module owns the token so there is exactly one answer to "where does the
 * token live" and "is it about to expire". Both questions used to have two
 * answers each — `lib/api.js` read `localStorage` then `sessionStorage` while
 * `authService.js` only ever wrote `sessionStorage`, and nothing anywhere
 * looked at `exp` — which is how an actively-used session could end up signed
 * out on a token that had quietly gone stale.
 *
 * Storage is `localStorage`, deliberately:
 *
 *   * `sessionStorage` is per tab. Opening the app in a second tab, or
 *     restoring a window after a browser restart, produced a tab with no token
 *     at all while Redux (persisted in `localStorage`) still said "signed in".
 *     The user saw the login screen despite holding a refresh cookie good for
 *     another week.
 *   * Every tab reading the same key means one refresh serves all of them, and
 *     the `storage` event lets a tab pick up a rotation another tab performed.
 *
 * This is not a weaker position against XSS than `sessionStorage` — script
 * running on the page reads either one. What actually bounds the damage is
 * that the token is short-lived (see JWT_ACCESS_EXP_SECONDS) and that the
 * refresh token it is renewed from is an httpOnly cookie JavaScript cannot
 * touch.
 */

const ACCESS_TOKEN_KEY = "accessToken";

// Renew this long before the token actually expires. Wide enough to cover a
// slow network and a sleeping tab's timer drift, so a request is never sent
// with a token that dies in flight.
const RENEW_LEAD_SECONDS = 120;

// Never schedule further out than this even if the token is long-lived, and
// never sooner than this, so a clock skew that makes every token look
// expired cannot turn into a refresh loop.
const MIN_RENEW_DELAY_MS = 5_000;
const MAX_RENEW_DELAY_MS = 10 * 60 * 1000;

const listeners = new Set();

const hasStorage = () => typeof window !== "undefined" && !!window.localStorage;

export const getAccessToken = () => (hasStorage() ? window.localStorage.getItem(ACCESS_TOKEN_KEY) : null);

export const setAccessToken = (token) => {
  if (!hasStorage()) return;
  if (token) window.localStorage.setItem(ACCESS_TOKEN_KEY, token);
  else window.localStorage.removeItem(ACCESS_TOKEN_KEY);
  listeners.forEach((cb) => cb(token || null));
};

export const clearAccessToken = () => setAccessToken(null);

/**
 * Subscribe to token changes, from this tab or any other. Returns an
 * unsubscribe function. The WebSocket client uses this to reconnect with a
 * fresh token the moment one is minted, instead of polling for it.
 */
export const onAccessTokenChange = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

if (typeof window !== "undefined") {
  // Fires only for changes made by *other* tabs, which is exactly the case the
  // in-process listener set cannot cover.
  window.addEventListener("storage", (event) => {
    if (event.key !== ACCESS_TOKEN_KEY) return;
    listeners.forEach((cb) => cb(event.newValue || null));
  });
}

/** Decode a JWT payload without verifying it. Signature checking is the
 * server's job; the client reads `exp` only to decide when to renew. */
export const decodeToken = (token) => {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const bytes = Uint8Array.from(window.atob(padded), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
};

/** Seconds until the token expires. `null` when there is no token or no `exp`
 * claim — the caller decides what an unknown expiry means. */
export const secondsUntilExpiry = (token = getAccessToken()) => {
  const exp = decodeToken(token)?.exp;
  if (typeof exp !== "number") return null;
  return exp - Math.floor(Date.now() / 1000);
};

/** True when the token is gone, unreadable, or inside the renewal lead time.
 * Treats an unreadable token as needing renewal: a token we cannot reason
 * about is one we should not be sending. */
export const needsRenewal = (token = getAccessToken()) => {
  if (!token) return true;
  const remaining = secondsUntilExpiry(token);
  if (remaining === null) return true;
  return remaining <= RENEW_LEAD_SECONDS;
};

/** Milliseconds to wait before renewing. Clamped at both ends — see the
 * constants above. */
export const msUntilRenewal = (token = getAccessToken()) => {
  const remaining = secondsUntilExpiry(token);
  if (remaining === null) return MIN_RENEW_DELAY_MS;
  return Math.min(MAX_RENEW_DELAY_MS, Math.max(MIN_RENEW_DELAY_MS, (remaining - RENEW_LEAD_SECONDS) * 1000));
};

export default {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
  onAccessTokenChange,
  decodeToken,
  secondsUntilExpiry,
  needsRenewal,
  msUntilRenewal,
};
